import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { QrCode, Search, Rocket, ArrowRight, Play, Pause, ChevronLeft, ChevronRight, Scan, User as UserIcon } from "lucide-react";
import { useState, useEffect, useMemo, type Dispatch, type SetStateAction } from "react";
import { getDemoProfile } from "@/utils/demoProfile";
import { optimizeCloudinaryImage } from "@/utils/imageOptimizer";
import { StickerPacksDrawer } from "@/components/stickers/StickerPacksDrawer";
import { MetefyQrAuthDrawer } from "@/components/auth/MetefyQrAuthDrawer";
import { supabase } from "@/integrations/supabase/client";
import { Capacitor } from "@capacitor/core";
import { useToast } from "@/components/ui/use-toast";
import { startGoogleWebOAuth } from "@/utils/googleWebAuth";

interface AboutHeroSectionProps {
  searchType?: "vehicleNumber" | "metroCard" | "metefyToken" | "username";
  setSearchType?: Dispatch<SetStateAction<"vehicleNumber" | "metroCard" | "metefyToken" | "username">>;
  searchValue?: string;
  setSearchValue?: (value: string) => void;
  handleSearch?: () => void;
  openDemoProfile?: (profile: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    x_username: string | null;
    bio: string | null;
    metro_card_number: string | null;
    created_at: string;
    updated_at: string;
    show_email: boolean;
    show_mobile: boolean;
    metefy_token: string | null;
    photo_url?: string | null;
    mask_bio?: boolean;
    social_sms?: string | null;
    social_call?: string | null;
    social_telegram?: string | null;
    social_whatsapp?: string | null;
    use_social_icons?: boolean;
  }) => void;
}

export function AboutHeroSection({
  searchType,
  setSearchType,
  searchValue,
  setSearchValue,
  handleSearch,
  openDemoProfile
}: AboutHeroSectionProps = {}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isGoogleSigningIn, setIsGoogleSigningIn] = useState(false);
  const [isFromDemo, setIsFromDemo] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isStickerDrawerOpen, setIsStickerDrawerOpen] = useState(false);
  const [isAuthDrawerOpen, setIsAuthDrawerOpen] = useState(false);
  const googleClientId = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % stickerImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + stickerImages.length) % stickerImages.length);
  };

  // Minimum swipe distance (in px) to trigger a swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    if (isPlaying) return;
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (isPlaying) return;
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (isPlaying || !touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextImage();
    } else if (isRightSwipe) {
      prevImage();
    }
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  // Original image URLs
  const originalStickerImages = [
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627381/Site_Posts_apdspu.png",
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627368/Site_Posts_2_ast368.png",
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627361/Site_Posts_7_xzu6fl.png",
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627365/Site_Posts_3_ovezi7.png",
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627375/Site_Posts_1_gypuwl.png",
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627365/Site_Posts_8_dgqyme.png",
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627381/Site_Posts_9_twcxeh.png",
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627363/Site_Posts_6_qvodxi.png"
  ];

  // Optimize images with Cloudinary transformations (600px width, 80% quality, auto format)
  const stickerImages = useMemo(() => {
    return originalStickerImages.map(url => optimizeCloudinaryImage(url, 600, 80, 'auto'));
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      setIsGoogleSigningIn(true);

      // Store source in localStorage for redirect handling - 'auth' redirects to profile page
      localStorage.setItem('metefy_oauth_source', 'auth');

      if (Capacitor.isNativePlatform()) {
        // NATIVE: Use Google's native sign-in SDK
        console.log('[AboutHero] Starting native Google Sign-In...');
        
        // Dynamically import GoogleAuth to avoid SSR issues
        const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');
        
        if (!googleClientId) {
          throw new Error('Google client ID is not configured');
        }

        try {
          // Initialize GoogleAuth with the Web Client ID (same one used by Supabase)
          await GoogleAuth.initialize({
            clientId: googleClientId,
            scopes: ['profile', 'email'],
            grantOfflineAccess: true
          });
        } catch (initError) {
          console.log('[AboutHero] GoogleAuth already initialized or init error:', initError);
        }
        
        // Sign in with Google natively
        const googleUser = await GoogleAuth.signIn();
        console.log('[AboutHero] Google Sign-In successful, got user:', googleUser.email);
        
        // Get the ID token from the Google sign-in result
        const idToken = googleUser.authentication.idToken;
        
        if (!idToken) {
          throw new Error('No ID token received from Google');
        }
        
        console.log('[AboutHero] Got ID token, signing into Supabase...');
        
        // Sign into Supabase using the Google ID token
        const { data, error } = await supabase.auth.signInWithIdToken({
          provider: 'google',
          token: idToken,
        });
        
        if (error) {
          console.error('[AboutHero] Supabase signInWithIdToken error:', error);
          throw error;
        }
        
        console.log('[AboutHero] Supabase sign-in successful!');
        
        if (data.session) {
          // Successfully signed in - navigate to profile page (same as auth page)
          navigate('/profile');
        }
      } else {
        // WEB: Use custom Google OAuth flow that returns an ID token
        if (!googleClientId) {
          throw new Error('Google client ID is not configured');
        }

        startGoogleWebOAuth();
      }
    } catch (error: any) {
      console.error('[AboutHero] Google Sign-In Error:', error);
      // Clean up localStorage on error
      localStorage.removeItem('metefy_oauth_source');
      toast({
        title: "Error signing in with Google",
        description: error?.message || "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGoogleSigningIn(false);
    }
  };

  // Preload only the first image for faster initial render
  useEffect(() => {
    if (stickerImages.length > 0) {
      const img = new Image();
      img.src = stickerImages[0];
    }
  }, [stickerImages]);

  // Handle the demo search when triggered
  useEffect(() => {
    if (isFromDemo && setSearchType && setSearchValue && handleSearch) {
      // First scroll to find owner section
      const element = document.getElementById('find-owner');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      // Set up the search parameters
      setSearchType('metefyToken');
      setSearchValue('981210013812');
    }
  }, [isFromDemo, setSearchType, setSearchValue]);

  // Watch for value changes and trigger search when ready
  useEffect(() => {
    if (isFromDemo && searchType === 'metefyToken' && searchValue === '981210013812' && handleSearch) {
      const timer = setTimeout(() => {
        handleSearch();
        setIsFromDemo(false);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isFromDemo, searchType, searchValue, handleSearch]);

  const handleScanDemo = () => {
    if (setSearchType && setSearchValue && handleSearch) {
      setIsFromDemo(true);
    } else {
      // Fallback to URL parameters if props are not available
      const searchParams = new URLSearchParams();
      searchParams.set('type', 'metefyToken');
      searchParams.set('number', '981210013812');
      window.history.replaceState(null, '', `${window.location.pathname}?${searchParams.toString()}`);
      window.location.reload();
    }
  };

  const handleOpenDemoProfile = () => {
    if (!openDemoProfile) return;
    openDemoProfile(getDemoProfile());
  };

  return (
    <section id="hero" className="py-16 pt-24 bg-background relative overflow-hidden">
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `
          linear-gradient(90deg, currentColor 1px, transparent 1px),
          linear-gradient(180deg, currentColor 1px, transparent 1px)
        `,
        backgroundSize: '24px 24px',
        color: 'var(--foreground)'
      }}></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          <div className="w-full lg:w-3/5">
            <h1 className="text-3xl lg:text-6xl font-bold mb-6 text-foreground">
              <span className="text-primary">Metefy QR Stickers</span> - Ensures finder can return your lost items!
            </h1>
            <p className="text-xl text-foreground/80 mb-8 max-w-2xl">
              Stick Metefy QR-Stickers on your products. If lost, finder can scan to reach you, making lost items recovery simple.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start relative z-30">
              {user ? (
                <Link
                  to="/profile"
                  className="bg-foreground hover:bg-foreground/90 text-background px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg inline-flex items-center justify-center gap-2 group cursor-pointer relative z-40"
                  aria-label="Go to your profile"
                >
                  <UserIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  Profile
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              ) : (
                <button
                  onClick={() => setIsAuthDrawerOpen(true)}
                  className="bg-foreground hover:bg-foreground/90 text-background px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg inline-flex items-center justify-center gap-2 group cursor-pointer relative z-40"
                  aria-label="Sign in or create an account to get started"
                >
                  <Rocket className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  Get Started
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              )}
              {/**
               * Original search-triggering Scan demo button (hidden for now; kept for later use)
               *
               * <button
               *   onClick={handleScanDemo}
               *   className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg inline-flex items-center justify-center gap-2 group cursor-pointer relative z-40"
               * >
               *   <Scan className="w-5 h-5 group-hover:scale-110 transition-transform" />
               *   Scan demo
               *   <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
               * </button>
               */}
              <button
                onClick={handleOpenDemoProfile}
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg inline-flex items-center justify-center gap-2 group cursor-pointer relative z-40"
              >
                <Scan className="w-5 h-5 group-hover:scale-110 transition-transform" />
                Try Demo
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          <div className="w-full lg:w-2/5 lg:max-w-2xl mt-12 lg:mt-0 relative z-10">
            <div className="relative flex flex-col">
              <div className="text-center mb-4 md:mb-6">
                <h2 className="text-2xl font-bold text-foreground mb-2 md:mb-4">
                  Stickers Showcase
                </h2>
              </div>
              <div className="relative">
              {/* Sticker Showcase Carousel */}
              <div className="mt-4 relative">
                {/* Fixed height container to prevent layout shift */}
                <div className="h-[300px] overflow-hidden relative">
                  {/* Play/Pause Toggle - fixed position */}
                  <button
                    onClick={togglePlayPause}
                    className="absolute bottom-4 right-4 z-20 bg-background/80 hover:bg-background text-foreground p-2 rounded-full shadow-lg transition-all duration-300 hover:scale-110 border border-border"
                    aria-label={isPlaying ? "Pause slideshow" : "Play slideshow"}
                  >
                    {isPlaying ? (
                      <Pause className="w-4 h-4" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                  </button>

                  {/* Navigation Arrows (visible when paused) */}
                  {!isPlaying && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-background/80 hover:bg-background text-foreground p-2 rounded-full shadow-lg transition-all duration-300 hover:scale-110 border border-border"
                        aria-label="Previous sticker"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      
                      <button
                        onClick={nextImage}
                        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-background/80 hover:bg-background text-foreground p-2 rounded-full shadow-lg transition-all duration-300 hover:scale-110 border border-border"
                        aria-label="Next sticker"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                    </>
                  )}

                  {isPlaying ? (
                    // Animated carousel when playing - reduced duplication for better performance
                    <div className="flex animate-scroll h-full">
                      {[
                        // Duplicate only twice for seamless infinite loop (reduced from 20x to 2x)
                        ...stickerImages,
                        ...stickerImages,
                  ].map((url, index) => (
                    <img
                      key={index}
                      src={url}
                      alt={`Metefy Sticker Design ${(index % stickerImages.length) + 1}`}
                      className="w-[300px] h-[300px] object-contain mx-4 rounded-lg flex-shrink-0"
                      width="300"
                      height="300"
                      loading={index < 2 ? "eager" : "lazy"}
                      decoding="async"
                    />
                  ))}
                    </div>
                  ) : (
                    // Manual navigation when paused
                    <div 
                      className="flex justify-center items-center h-full touch-pan-y"
                      onTouchStart={onTouchStart}
                      onTouchMove={onTouchMove}
                      onTouchEnd={onTouchEnd}
                    >
                      <div className="w-[300px] h-[300px] relative mx-4">
                        {stickerImages.map((src, index) => (
                          <img
                            key={index}
                            src={src}
                            alt={`Metefy Sticker Design ${index + 1}`}
                            className={`w-[300px] h-[300px] object-contain rounded-lg transition-opacity duration-300 select-none absolute top-0 left-0 ${
                              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                            }`}
                            width="300"
                            height="300"
                            loading={index === 0 ? "eager" : "lazy"}
                            decoding="async"
                            draggable={false}
                            fetchPriority={index === 0 ? "high" : "auto"}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Dots Indicator (visible when paused) - fixed position */}
                <div className="h-8 flex items-center justify-center">
                  {!isPlaying && (
                    <div className="flex gap-2">
                      {stickerImages.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`w-3 h-3 rounded-full transition-all duration-300 ${
                            index === currentImageIndex 
                              ? 'bg-primary scale-110' 
                              : 'bg-muted hover:bg-muted-foreground/50'
                          }`}
                          aria-label={`Go to sticker ${index + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

                {/* Buy Stickers Button */}
                <div className="flex justify-center mt-8">
                  <button 
                    onClick={() => setIsStickerDrawerOpen(true)}
                    className="bg-foreground hover:bg-foreground/90 text-background px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg inline-flex items-center justify-center gap-2 group cursor-pointer"
                  >
                    <QrCode className="w-5 h-5 group-hover:scale-110 transition-transform" />
                    Buy Stickers
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <StickerPacksDrawer isOpen={isStickerDrawerOpen} setIsOpen={setIsStickerDrawerOpen} openDemoProfile={openDemoProfile} />
      <MetefyQrAuthDrawer isOpen={isAuthDrawerOpen} setIsOpen={setIsAuthDrawerOpen} />
    </section>
  );
}
